import { useCallback, useMemo } from "react";
import { showToast } from "@int-traderjoes/ui-components-library";
import { useLanguageContext } from "@/context/LanguageContext";
import { DEFAULT_FILE_SIZE } from "@/utils/constants";
import useTranslation from "./useTranslation";

type UseFileValidatorParams = {
  allowedTypes: string[];
  maxSize?: number;
};

/** Maps file extensions (and legacy aliases) to browser MIME types. */
const EXTENSION_MIME_TYPES: Record<string, string[]> = {
  pdf: ["application/pdf"],
  doc: ["application/msword"],
  docx: ["application/vnd.openxmlformats-officedocument.wordprocessingml.document"],
  xls: ["application/vnd.ms-excel"],
  xlsx: ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
  ppt: ["application/vnd.ms-powerpoint"],
  pptx: ["application/vnd.openxmlformats-officedocument.presentationml.presentation"],
  txt: ["text/plain"],
  rtf: ["application/rtf", "text/rtf"],
  csv: ["text/csv", "application/csv", "text/x-csv", "application/x-csv"],
  odt: ["application/vnd.oasis.opendocument.text"],
  ods: ["application/vnd.oasis.opendocument.spreadsheet"],
  odp: ["application/vnd.oasis.opendocument.presentation"],
  wps: [
    "application/vnd.ms-works",
    "application/vnd.wps-office.wps",
    "application/msworks",
  ],
  jpg: ["image/jpeg"],
  jpeg: ["image/jpeg"],
  png: ["image/png"],
  bmp: ["image/bmp", "image/x-ms-bmp"],
  tiff: ["image/tiff"],
  tif: ["image/tiff"],
  webp: ["image/webp"],
  svg: ["image/svg+xml"],
  heic: ["image/heic", "image/heic-sequence"],
  heif: ["image/heif", "image/heif-sequence"],
  gif: ["image/gif"],
  ai: [
    "application/postscript",
    "application/vnd.adobe.illustrator",
    "application/illustrator",
  ],
  eps: ["application/postscript", "image/x-eps", "application/eps"],
  word: [
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ],
  excel: [
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ],
};

function buildAllowedFileRules(allowedTypes: string[]) {
  const mimeTypes = new Set<string>();
  const extensions = new Set<string>();

  for (const type of allowedTypes) {
    if (type === "*") {
      continue;
    }

    if (type.includes("/")) {
      mimeTypes.add(type.toLowerCase());
      continue;
    }

    const key = type.toLowerCase();
    extensions.add(key);

    const mapped = EXTENSION_MIME_TYPES[key];
    if (mapped) {
      mapped.forEach((mime) => mimeTypes.add(mime.toLowerCase()));
    }
  }

  return { mimeTypes, extensions };
}

function isAllowedFile(
  file: File,
  mimeTypes: Set<string>,
  extensions: Set<string>
): boolean {
  const extension = file.name.split(".").pop()?.toLowerCase();
  const fileMime = file.type?.toLowerCase() ?? "";

  if (extension && extensions.has(extension)) {
    return true;
  }

  if (
    fileMime &&
    fileMime !== "application/octet-stream" &&
    mimeTypes.has(fileMime)
  ) {
    return true;
  }

  return false;
}

export function useFileValidator({
  allowedTypes,
  maxSize = DEFAULT_FILE_SIZE,
}: UseFileValidatorParams) {
  const { mimeTypes, extensions } = useMemo(
    () => buildAllowedFileRules(allowedTypes),
    [allowedTypes]
  );
  const { languageText } = useLanguageContext();
  const { t } = useTranslation(languageText?.MESSAGES?.VALIDATION_MESSAGES);

  const validateFile = useCallback(
    (file: File): boolean => {
      // Skip type check if wildcard is present
      if (allowedTypes?.includes("*")) {
        const maxSizeBytes = maxSize * 1024 * 1024;
        if (file.size > maxSizeBytes) {
          showToast({
            type: "error",
            message: t("FILE_TOO_LARGE", { size: maxSize }),
          });
          return false;
        }
        return true;
      }

      if (!isAllowedFile(file, mimeTypes, extensions)) {
        const readableTypes = allowedTypes?.join(", ");

        showToast({
          type: "error",
          message: t("INVALID_FILE_TYPE", { types: readableTypes }),
        });
        return false;
      }

      const maxSizeBytes = maxSize * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        showToast({
          type: "error",
          message: t("FILE_TOO_LARGE", { size: maxSize }),
        });
        return false;
      }

      return true;
    },
    [allowedTypes, extensions, maxSize, mimeTypes, t]
  );

  return validateFile;
}
